import { apiClient } from './client';
import type { Notification, NotificationList, NotificationTypeValue } from '@/types/notification';
import { NotificationType } from '@/types/notification';

export interface NotificationPreferences {
  id?: number;
  user_id?: number;
  email_messages: boolean;
  email_profile_visits: boolean;
  email_matches: boolean;
  email_likes: boolean;
  email_follow_ups: boolean;
  email_system: boolean;
  email_purchases: boolean;
  
  push_messages: boolean;
  push_profile_visits: boolean;
  push_matches: boolean;
  push_likes: boolean;
  push_follow_ups: boolean;
  push_system: boolean;
  push_purchases: boolean;
  
  email_batch_enabled: boolean;
  email_batch_interval_minutes: number;
  
  quiet_hours_enabled: boolean;
  quiet_hours: {
    start: string;
    end: string;
    timezone?: string;
  } | null;
}

export interface ToastSettings {
  toast_duration_ms: number;
  toast_position: string;
  toast_max_visible: number;
  types_enabled: string[];
}

export const notificationService = {
  async getNotifications(params?: {
    limit?: number;
    offset?: number;
    unread_only?: boolean;
    notification_type?: NotificationTypeValue;
  }): Promise<NotificationList> {
    const { data } = await apiClient.get<NotificationList>('/notifications', { params });
    return data;
  },

  async getUnreadCount(): Promise<{ unread_count: number }> {
    const { data } = await apiClient.get<{ unread_count: number }>('/notifications/unread-count');
    return data;
  },

  async markAsRead(notificationIds: number[]): Promise<{ updated: number }> {
    const { data } = await apiClient.put<{ updated: number }>('/notifications/mark-read', notificationIds);
    return data;
  },

  async markAllAsRead(): Promise<{ updated: number }> {
    const { data } = await apiClient.put<{ updated: number }>('/notifications/mark-all-read');
    return data;
  },

  async deleteNotification(notificationId: number): Promise<{ success: boolean }> {
    const { data } = await apiClient.delete<{ success: boolean }>(`/notifications/${notificationId}`);
    return data;
  },

  async createTestNotification(): Promise<Notification> {
    const { data } = await apiClient.post<Notification>('/notifications/test');
    return data;
  },
  
  async markSessionNotificationsRead(sessionId: string): Promise<{ updated: number }> {
    const { data } = await apiClient.put<{ updated: number }>(`/notifications/session/${sessionId}/mark-read`);
    return data;
  },
  
  async markCharacterNotificationsRead(characterId: number): Promise<{ updated: number }> {
    const { data } = await apiClient.put<{ updated: number }>(`/notifications/character/${characterId}/mark-read`);
    return data;
  },
  
  async getToastSettings(): Promise<ToastSettings> {
    const { data } = await apiClient.get<ToastSettings>('/notifications/settings');
    return data;
  },
  
  async getPreferences(): Promise<NotificationPreferences> {
    const { data } = await apiClient.get<NotificationPreferences>('/notifications/preferences');
    return data;
  },
  
  async updatePreferences(preferences: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
    const { data } = await apiClient.put<NotificationPreferences>('/notifications/preferences', preferences);
    return data;
  },
  
  async toggleAllNotifications(enabled: boolean, channel?: 'email' | 'push'): Promise<void> {
    await apiClient.post('/notifications/preferences/toggle-all', { enabled, channel });
  },
};